import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import { ApiError, api, type SignalState, type Source } from "../api/client";
import "../components/ld-signal";
import { birthInputToApi, formatDate } from "../format";
import { icons } from "../icons";
import { navigate } from "../router";
import { SOURCE_STATUS, STATES, healthSummary } from "../signal-state";
import { shared } from "../styles/shared";

@customElement("page-search")
export class PageSearch extends LitElement {
  @state() private sources: Source[] = [];
  @state() private error = "";
  @state() private busy = false;

  static styles = [
    shared,
    css`
      :host { display: block; }
      .hero { padding: 88px 0 72px; text-align: center; position: relative; overflow: hidden; }
      .hero::before {
        content: ""; position: absolute; inset: -20% -10% auto -10%; height: 520px; z-index: -1;
        background: radial-gradient(60% 60% at 50% 20%, var(--blue-50) 0%, rgba(245, 249, 255, 0) 70%);
      }
      h1 { font-size: clamp(36px, 5.4vw, 58px); font-weight: 800; letter-spacing: -.03em; line-height: 1.05; color: var(--navy-950); max-width: 780px; margin: 0 auto 20px; }
      h1 em { font-style: normal; background: linear-gradient(100deg, var(--blue-600), #6aa3ff); -webkit-background-clip: text; background-clip: text; color: transparent; }
      .sub { font-size: 17px; color: var(--slate-600); max-width: 560px; margin: 0 auto 44px; line-height: 1.6; }
      .panel { max-width: 780px; margin: 0 auto; background: var(--white); border: 1px solid var(--slate-200); border-radius: 20px; box-shadow: var(--shadow-lg); padding: 22px; text-align: left; }
      .grid { display: grid; grid-template-columns: 1.1fr 1.1fr .9fr; gap: 12px; margin-bottom: 14px; }
      @media (max-width: 720px) { .grid { grid-template-columns: 1fr; } .hero { padding: 48px 0; } }
      .panel .btn-primary { padding: 13px 20px; font-size: 15px; }
      .meta { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding-top: 14px; margin-top: 14px; border-top: 1px dashed var(--slate-200); font-size: 12.5px; color: var(--slate-400); }
      .meta strong { color: var(--slate-600); font-weight: 600; }
      .panel .alert { margin-bottom: 14px; }
      .health { max-width: 780px; margin: 16px auto 0; text-align: left; }
      .health ul { margin: 6px 0 0; padding-left: 18px; list-style: disc; }
      .health li { margin-top: 2px; }
      .section { padding: 72px 0 32px; }
      .section-head { max-width: 640px; margin: 0 auto 36px; text-align: center; }
      .section-head h2 { font-size: clamp(26px, 3.4vw, 34px); font-weight: 800; letter-spacing: -.02em; color: var(--navy-950); margin-bottom: 12px; }
      .section-head p { color: var(--slate-600); font-size: 15px; }
      .states { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; }
      @media (max-width: 940px) { .states { grid-template-columns: repeat(2, 1fr); } }
      @media (max-width: 520px) { .states { grid-template-columns: 1fr; } }
      .state { padding: 20px 16px; border-radius: var(--r-md); border: 1px solid var(--slate-200); background: var(--white); }
      .state h3 { font-size: 13.5px; font-weight: 700; margin: 12px 0 6px; }
      .state p { font-size: 12.5px; color: var(--slate-600); line-height: 1.5; }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    try {
      this.sources = await api.sources();
    } catch {
      this.sources = [];
    }
  }

  firstUpdated() {
    this.renderRoot.querySelector<HTMLInputElement>("#last_name")?.focus();
  }

  private async submit(event: SubmitEvent) {
    event.preventDefault();
    const data = new FormData(event.target as HTMLFormElement);
    const birth = birthInputToApi(String(data.get("birth") ?? ""));
    if (birth === undefined) {
      this.error = "Date de naissance attendue au format AAAA ou JJ/MM/AAAA.";
      return;
    }
    this.busy = true;
    this.error = "";
    try {
      const screening = await api.createScreening({
        last_name: String(data.get("last_name") ?? ""),
        first_name: String(data.get("first_name") ?? "") || null,
        birth_date: birth,
      });
      navigate(`/rapports/${screening.id}`);
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : "La recherche a échoué.";
    } finally {
      this.busy = false;
    }
  }

  render() {
    const register = this.sources.find((s) => s.code === "GDA");
    // Warn before searching rather than letting grey signals surprise the analyst.
    const troubled = this.sources.filter((s) => SOURCE_STATUS[s.health.status].problem);
    return html`
      <section class="hero">
        <div class="container">
          <h1 class="fade-up">Lancer la <em>recherche.</em></h1>
          <p class="sub fade-up">Laundering interroge plusieurs sources en parallèle puis centralise les résultats dans un rapport exploitable.</p>

          <form class="panel fade-up" @submit=${this.submit} novalidate>
            ${this.error ? html`<div class="alert alert-error" role="alert">${icons.alert}<span>${this.error}</span></div>` : nothing}
            <div class="grid">
              <div class="field">
                <label for="last_name">Nom</label>
                <input class="input" id="last_name" name="last_name" placeholder="ex. Vezanov" required maxlength="100" autocomplete="off" />
              </div>
              <div class="field">
                <label for="first_name">Prénom <span class="muted">(recommandé)</span></label>
                <input class="input" id="first_name" name="first_name" placeholder="ex. Alban" maxlength="100" autocomplete="off" />
              </div>
              <div class="field">
                <label for="birth">Naissance <span class="muted">(facultatif)</span></label>
                <input class="input" id="birth" name="birth" placeholder="JJ/MM/AAAA ou AAAA" inputmode="numeric" autocomplete="off" />
              </div>
            </div>
            <button class="btn btn-primary btn-block" ?disabled=${this.busy}>
              ${this.busy ? html`<span class="spinner"></span> Interrogation des sources…` : html`${icons.search} Lancer la recherche`}
            </button>
            <div class="meta">
              <span><strong>${this.sources.length || "—"}</strong> sources interrogées en parallèle</span>
              <span>Registre du gel des avoirs : <strong>${register?.version ? formatDate(register.version) : "indisponible"}</strong></span>
            </div>
          </form>

          ${troubled.length
            ? html`<div class="alert alert-info health" role="status">
                ${icons.info}
                <div>
                  <strong>Certaines sources sont actuellement en difficulté</strong> : leurs voyants seront gris.
                  <ul>${troubled.map((s) => html`<li>${s.label} — ${healthSummary(s.health)}</li>`)}</ul>
                </div>
              </div>`
            : nothing}
        </div>
      </section>

      <section class="section">
        <div class="container">
          <div class="section-head">
            <h2>Lire les voyants</h2>
            <p>Les voyants signalent, l'enquêteur conclut. Un voyant gris n'est jamais une absence de résultat : la source n'a pas pu être consultée.</p>
          </div>
          <div class="states">
            ${(Object.keys(STATES) as SignalState[]).map(
              (key) => html`<div class="state">
                <ld-signal .code=${STATES[key].label} .state=${key} compact></ld-signal>
                <h3>${STATES[key].label}</h3>
                <p>${STATES[key].description}</p>
              </div>`,
            )}
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-search": PageSearch;
  }
}
